import { ChatMessageListenerSource } from "@/pages/LiveChat/types/chat";

export interface SocketMessage {
  message_id: number;
  sender: string;
  sender_name?: string;
  message: string;
  created_at: string;
  chat_id: string;
  document_id?: string | null;
  parent_id?: string | null;
  replies?: SocketMessage[];
}

export interface WebSocketParams {
  roomId: string;
  threadId?: string;
  documentId?: string;
  from?: ChatMessageListenerSource | null;
}

let sockets: { [key: string]: WebSocket } = {};
const webSocketHandler = () => {
  const endpoint = process.env.REACT_APP_WS_BASE_URL;

  const getToken = (): string => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Token not found");
    }
    return token;
  };

  const getSocketUrl = ({
    roomId,
    threadId,
    documentId,
  }: WebSocketParams): string => {
    const token = getToken();
    let url = `${endpoint}/ws/chat/${roomId}/?token=${token}`;

    if (threadId) {
      url += `&parent_id=${threadId}`;
    }

    if (documentId) {
      url += `&document_id=${documentId}`;
    }

    return url;
  };

  const getSocketKey = ({
    roomId,
    threadId,
    documentId,
    from,
  }: WebSocketParams): string => {
    let key = `room_${roomId}`;
    if (threadId) key += `_thread_${threadId}`;
    if (documentId) key += `_doc_${documentId}`;
    if (from) key += `_from_${from}`;
    return key;
  };

  const initializeSocket = (params: WebSocketParams): Promise<WebSocket> => {
    return new Promise((resolve, reject) => {
      const socketKey = getSocketKey(params);

      if (!sockets[socketKey]) {
        try {
          const url = getSocketUrl(params);
          const ws = new WebSocket(url);
          sockets[socketKey] = ws;

          ws.onopen = () => {
            sockets[socketKey] = ws;
            console.log(`WebSocket connected for ${socketKey}`);
            resolve(ws);
          };

          ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            handleClosing(params);
            reject(error);
          };
        } catch (error) {
          reject(error);
        }
      } else {
        resolve(sockets[socketKey]);
      }
    });
  };

  const handleListening = (
    params: WebSocketParams,
    callback: (data: SocketMessage) => void,
  ): Promise<{ close?: () => void } | undefined> => {
    return new Promise(async (resolve, reject) => {
      try {
        let isClosed = false;
        if (!params.roomId) {
          console.warn("Room ID is required");
          resolve({});
          return;
        }
        // TODO: Uncomment this when the backend is ready
        const ws = await initializeSocket(params);
        ws.onmessage = (event) => {
          if (isClosed) return;
          const data = JSON.parse(event.data);
          callback(data);
        };
        ws.onclose = () => {
          isClosed = true;
          const socketKey = getSocketKey(params);
          console.log(`WebSocket closed for ${socketKey}`);
          delete sockets[socketKey];
        };
        resolve({
          close: () => {
            isClosed = true;
          },
        });
      } catch (error) {
        console.error("Error connecting to WebSocket:", error);
        reject(error);
      }
    });
  };

  const handleSending = (
    params: WebSocketParams,
    message: string,
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        const ws = await initializeSocket(params);
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ message }));
          resolve();
        } else {
          console.error("WebSocket is not open. ReadyState:", ws.readyState);
          reject(new Error("WebSocket is not open"));
        }
      } catch (error) {
        console.error("Error sending message:", error);
        reject(error);
      }
    });
  };

  const handleClosing = (params: WebSocketParams): void => {
    const socketKey = getSocketKey(params);
    if (sockets[socketKey]) {
      sockets[socketKey].close();
      delete sockets[socketKey];
    }
  };

  const closeAllSockets = (): void => {
    Object.keys(sockets).forEach((key) => {
      sockets[key].close();
      delete sockets[key];
    });
  };

  return {
    handleListening,
    handleSending,
    handleClosing,
    closeAllSockets,
  };
};

export default webSocketHandler;
